import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiError, apiAction } from '../lib/api';

type Status =
  | { kind: 'working' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const ERROR_COPY: Record<string, string> = {
  token_expired: 'This invite link has expired. Ask the admin to send a new one.',
  token_already_used: 'This invite has already been used.',
  token_invalid: 'This invite link is invalid.',
};

interface Props {
  onAccepted: () => void;
}

export default function Accept({ onAccepted }: Props) {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>({ kind: 'working' });
  const ran = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'error', message: 'Missing token' });
      return;
    }
    if (ran.current) return;
    ran.current = true;

    apiAction('/api/users/invite/accept', { token })
      .then(() => {
        setStatus({ kind: 'success' });
        onAccepted();
      })
      .catch((err) => {
        const code = err instanceof ApiError ? err.message : 'token_invalid';
        const message = ERROR_COPY[code] ?? 'Could not accept invite.';
        setStatus({ kind: 'error', message });
      });
  }, [token, onAccepted]);

  if (status.kind === 'success') return <Navigate to="/" replace />;

  return (
    <div className="login-shell">
      <div className="card login-card">
        {status.kind === 'working' ? (
          <>
            <h1>Accepting invite…</h1>
            <p className="subtle">One moment.</p>
          </>
        ) : (
          <>
            <h1>Can't accept invite</h1>
            <p className="subtle">{status.message}</p>
            <p style={{ marginTop: 16 }}>
              <a href="/login">Go to sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
